/**
 * Phase 266e — Referrer Thank-You dialog.
 *
 * One-click outreach modal opened from the Embed Performance tile.
 * Owner enters the partner editor's email + an optional custom note,
 * we POST it through the real /thank-you endpoint (which sends via the
 * existing _send_academy_email fail-safe relay and enforces 7-day per
 * (academy, host) cooldown + host-must-be-a-real-referrer anti-spam).
 */
import { useState } from 'react';
import { Heart, Send, Loader2 } from 'lucide-react';
import api from '@/lib/api';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';

export default function ReferrerThankYouDialog({ open, onOpenChange, host, viewCount, onSent }) {
  const [email, setEmail] = useState('');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const reset = () => { setEmail(''); setNote(''); setSubmitting(false); };

  const handleOpenChange = (next) => {
    if (!next) reset();
    onOpenChange(next);
  };

  const handleSend = async () => {
    if (!email.trim()) {
      toast.error('Please enter the editor\'s email address.');
      return;
    }
    if (!email.includes('@')) {
      toast.error('That doesn\'t look like a valid email address.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post('/academy/top-moments-pixel/thank-you', {
        target_host: host,
        contact_email: email.trim(),
        custom_note: note.trim() || null,
      });
      toast.success(res?.data?.message || `Thank-you sent to ${email.trim()}`);
      if (onSent) onSent(host);
      handleOpenChange(false);
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Failed to send thank-you email.';
      toast.error(detail);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="referrer-thankyou-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Heart className="h-4 w-4 text-rose-500" />
            Thank {host}
          </DialogTitle>
          <DialogDescription>
            {viewCount
              ? `${host} sent ${viewCount} ${viewCount === 1 ? 'view' : 'views'} to your wall. A short note from you = a real relationship.`
              : `Send a short thank-you note to ${host}.`}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 pt-2">
          <div className="space-y-1.5">
            <Label htmlFor="thankyou-email" className="text-xs font-semibold">
              Editor / contact email
            </Label>
            <Input
              id="thankyou-email"
              type="email"
              placeholder={`editor@${host}`}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoFocus
              data-testid="referrer-thankyou-email"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="thankyou-note" className="text-xs font-semibold">
              Personal note <span className="font-normal text-slate-400">(optional)</span>
            </Label>
            <Textarea
              id="thankyou-note"
              placeholder="e.g. Loved your match recap last week — the dribble compilation was perfect."
              rows={3}
              maxLength={1000}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              data-testid="referrer-thankyou-note"
            />
            <div className="text-[10px] text-slate-400 text-right tabular-nums">{note.length}/1000</div>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={submitting}
            data-testid="referrer-thankyou-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSend}
            disabled={submitting || !email.trim()}
            className="bg-rose-600 hover:bg-rose-700"
            data-testid="referrer-thankyou-send"
          >
            {submitting ? (
              <><Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" />Sending…</>
            ) : (
              <><Send className="h-3.5 w-3.5 mr-2" />Send thank-you</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
